import type { TestLandingConfig } from "./config.js";
import { GENERATED_TEST_LANDING_CONFIGS } from "./configs.generated.js";

/**
 * The 4 bespoke standardized-test landing configs — hand-authored with
 * distinct, math-focused content, kept FIRST so they stay easy to find and
 * edit. The 61 generated configs (national + per-state) live in
 * `configs.generated.ts` and are concatenated after these.
 *
 * Adding a new test in the future is just appending one config object here (or
 * to the generated file): the dynamic `/:testSlug` route, the prerender loop,
 * and the regression test all derive from `TEST_LANDING_CONFIGS`.
 */
const BESPOKE_TEST_LANDING_CONFIGS: TestLandingConfig[] = [
  // ── STAAR ──────────────────────────────────────────────────────────────
  {
    slug: "staar",
    fullName: "State of Texas Assessments of Academic Readiness (STAAR)",
    shortName: "STAAR",
    scope: "Texas",
    state: "Texas",
    sourceUrl: "https://tea.texas.gov/student-assessment/testing/staar",
    seo: {
      title: "STAAR Math Prep for Kids | Sports Academy Games",
      description:
        "Help your Texas student prepare for the STAAR math test (grades 3–8 + Algebra I EOC) with fun, adaptive games built on the TEKS. No worksheets, no test anxiety.",
    },
    heroTag: "Texas · STAAR Math Prep",
    heroHeadline: "Does your child need help preparing for the STAAR math test?",
    heroSubhead:
      "The STAAR is Texas's annual math assessment, given every spring from grade 3 through the Algebra I end-of-course exam. Sports Academy Games turns TEKS-aligned math practice into fast games kids actually want to play — so STAAR week feels like a warm-up, not a wall.",
    gradeRange: "Grades 3–8 + Algebra I EOC",
    heroMeta: "Grades 3–8 + Algebra I EOC · Texas Education Agency · TEKS-aligned",
    topics: {
      eyebrow: "What the test covers",
      title: "STAAR math, grade by grade",
      lede: "STAAR math is built on the Texas Essential Knowledge and Skills (TEKS). The reporting categories shift as students move up — our games drill each band.",
      groups: [
        {
          label: "Grades 3–5",
          items: [
            "Numerical representations & place value",
            "Computations & algebraic relationships",
            "Geometry & measurement",
            "Data analysis & personal financial literacy",
          ],
        },
        {
          label: "Grades 6–8",
          items: [
            "Proportional reasoning & ratios",
            "Expressions, equations & relationships",
            "Two-variable relationships & functions",
            "Measurement & data",
          ],
        },
        {
          label: "Algebra I EOC",
          items: [
            "Linear functions, equations & inequalities",
            "Quadratic functions & equations",
            "Exponential functions",
            "Number & algebraic methods",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn STAAR prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which TEKS your child has and hasn't mastered — by reporting category, not just an overall score.",
        "Short, game-based drills target the precise STAAR reporting categories for your child's grade — 60–90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress through the spring, so practice stays aligned to where your child actually needs it before test day.",
      ],
    },
    faq: {
      eyebrow: "Texas parents ask",
      title: "STAAR questions, answered",
      items: [
        {
          q: "When is the STAAR math test given?",
          a: "STAAR is administered each spring. Grades 3–8 math testing windows fall in late spring, and the Algebra I end-of-course exam has spring, summer, and winter windows. Building a little math practice into the weeks before your window beats cramming.",
        },
        {
          q: "Which grades take a STAAR math test?",
          a: "Every Texas student in grades 3 through 8 takes a STAAR math assessment, and students take the Algebra I STAAR end-of-course exam when they complete the course (usually grade 8 or 9).",
        },
        {
          q: "Is the STAAR aligned to the TEKS?",
          a: "Yes — STAAR measures the Texas Essential Knowledge and Skills. Every game in our catalog drills math skills that map to TEKS reporting categories, so practice time is test-relevant time.",
        },
        {
          q: "Do you teach to the test?",
          a: "No. We build genuine math mastery through play. Because STAAR measures real TEKS skills, mastering the math is the best STAAR prep there is — no drilling of question formats required.",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your Texan stands before",
      titleStripe: "STAAR.",
      sub: "Take a free adaptive diagnostic — no signup, no card. We'll map your child to the right TEKS level and the right games.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
  },

  // ── MAP Growth ─────────────────────────────────────────────────────────
  {
    slug: "map",
    fullName: "MAP Growth (Measures of Academic Progress)",
    shortName: "MAP Growth",
    scope: "national",
    sourceUrl: "https://www.nwea.org/map-growth/",
    seo: {
      title: "MAP Growth Math Prep for Kids | Sports Academy Games",
      description:
        "Help your child prepare for the NWEA MAP Growth math test with fun, adaptive games. Understand the RIT scale and the fall/winter/spring growth windows. Grades K–12.",
    },
    heroTag: "National Test · MAP Growth Math Prep",
    heroHeadline: "Does your child need help preparing for the MAP Growth math test?",
    heroSubhead:
      "MAP Growth from NWEA is an adaptive math test given up to three times a year — it measures how much your child grows, not just where they land. Sports Academy Games builds that growth with fast, adaptive math games kids actually choose to play.",
    gradeRange: "Grades K–12",
    heroMeta: "Grades K–12 · NWEA · Adaptive · RIT scale",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on MAP Growth",
      lede: "MAP Growth adapts to your child as they go, drawing from across the K–12 math strands. Our games cover the same domains, split by grade band.",
      groups: [
        {
          label: "Early grades (K–5)",
          items: [
            "Number sense & operations",
            "Computation & algebraic thinking",
            "Measurement & data",
            "Geometry",
          ],
        },
        {
          label: "Upper grades (6–12)",
          items: [
            "Ratios, proportions & number systems",
            "Expressions, equations & functions",
            "Geometry & measurement",
            "Statistics & probability",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Build growth, not test anxiety",
      points: [
        "A free adaptive diagnostic finds your child's real level across math strands — the same kind of adaptive read MAP Growth gives.",
        "Short, game-based drills target the exact skills just above your child's current level — where real RIT growth happens.",
        "Mastery tracking lets you see progress between the fall, winter, and spring windows, so each MAP season shows movement.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "How MAP Growth actually works",
      cards: [
        {
          title: "It's a RIT score, not a grade",
          body: "MAP reports a RIT score on a stable, equal-interval scale. A RIT score isn't a percentage or a letter grade — it's a point on a growth ladder, so a student's RIT in fall and spring can be compared directly to see how far they climbed.",
        },
        {
          title: "It's adaptive",
          body: "Questions get harder when your child answers correctly and easier when they don't, so the test quickly homes in on their true level. That's exactly how our diagnostic and our games scale difficulty too.",
        },
        {
          title: "Three windows a year",
          body: "Most schools test in fall, winter, and spring. The point isn't a single high-stakes score — it's the growth between windows, which is why steady practice between tests matters more than cramming before one.",
        },
        {
          title: "It measures growth, not a cut score",
          body: "Because MAP is about growth over time, the best preparation is consistent skill-building. Mastering the underlying math is what moves the RIT score — and that's what our games are built to do.",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See your child's level before the next",
      titleStripe: "MAP.",
      sub: "Take a free adaptive diagnostic — no signup, no card. We'll find your child's level and the games that grow it.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
  },

  // ── SAT / PSAT ───────────────────────────────────────────────────────────
  {
    slug: "sat",
    fullName: "SAT (Scholastic Assessment Test)",
    shortName: "SAT",
    scope: "national",
    sourceUrl: "https://satsuite.collegeboard.org/sat",
    seo: {
      title: "SAT Math Prep for Students | Sports Academy Games",
      description:
        "Help your student prepare for the digital SAT math section with fun, adaptive games. Covers all four College Board math domains plus the calculator-allowed, adaptive format. Grades 9–12.",
    },
    heroTag: "National Test · SAT Math Prep",
    heroHeadline: "Does your student need help preparing for the SAT math section?",
    heroSubhead:
      "The digital SAT math section is adaptive, calculator-allowed throughout, and built on four College Board domains. Sports Academy Games turns that math into fast, fun games — so practice builds real fluency instead of test dread.",
    gradeRange: "Grades 9–12",
    heroMeta: "Grades 9–12 · College Board · Digital & adaptive",
    topics: {
      eyebrow: "What the test covers",
      title: "The four SAT math domains",
      lede: "The digital SAT math section is built on four College Board content domains. Our games drill every one of them.",
      groups: [
        {
          label: "College Board math domains",
          items: [
            "Algebra — linear equations, systems & inequalities",
            "Advanced Math — quadratics, polynomials & nonlinear functions",
            "Problem-Solving & Data Analysis — ratios, rates, percentages & statistics",
            "Geometry & Trigonometry — area, volume, angles & right triangles",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn SAT prep into play",
      points: [
        "A free adaptive diagnostic pinpoints which of the four SAT math domains your student has and hasn't mastered.",
        "Short, game-based drills target the exact SAT math skills — fluency with the underlying math is what raises a score, not memorizing question types.",
        "Mastery tracking shows progress over time, so SAT prep is steady skill-building rather than a panicked weekend before test day.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "The digital SAT format",
      cards: [
        {
          title: "It's fully digital",
          body: "The SAT is taken on a computer or tablet through the Bluebook app. The math section is delivered in two modules, and the on-screen tools include a built-in graphing calculator.",
        },
        {
          title: "Two adaptive modules",
          body: "Math is split into two modules. How your student does on the first module determines the difficulty of the second — so the test adapts to performance, much like our own diagnostic.",
        },
        {
          title: "Calculator allowed throughout",
          body: "Unlike the old paper SAT, a calculator is permitted on the entire math section, and a graphing calculator is built into the testing app. Strong mental math still helps you move faster and check work.",
        },
        {
          title: "The PSAT is the on-ramp",
          body: "The PSAT/NMSQT covers the same math domains in a lower-stakes setting and feeds into National Merit. Practicing the same skills prepares a student for both the PSAT and the SAT that follows.",
        },
      ],
    },
    faq: {
      eyebrow: "Students & parents ask",
      title: "SAT math questions, answered",
      items: [
        {
          q: "Can you use a calculator on the SAT math section?",
          a: "Yes. On the digital SAT, a calculator is allowed on the entire math section, and the Bluebook testing app includes a built-in graphing calculator. Building fluency still helps you work faster and avoid leaning on the calculator for every step.",
        },
        {
          q: "What's the difference between the PSAT and the SAT?",
          a: "The PSAT/NMSQT is a lower-stakes practice test that also qualifies juniors for National Merit. It covers the same math domains as the SAT, so prep for one prepares you for the other — think of the PSAT as the SAT's on-ramp.",
        },
        {
          q: "What math is on the SAT?",
          a: "Four College Board domains: Algebra, Advanced Math, Problem-Solving & Data Analysis, and Geometry & Trigonometry. Our games drill all four.",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your student stands before the",
      titleStripe: "SAT.",
      sub: "Take a free adaptive diagnostic — no signup, no card. We'll map the SAT math domains your student should sharpen.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
  },

  // ── i-Ready ──────────────────────────────────────────────────────────────
  {
    slug: "iready",
    fullName: "i-Ready Diagnostic (Curriculum Associates)",
    shortName: "i-Ready",
    scope: "national",
    sourceUrl: "https://www.curriculumassociates.com/products/i-ready",
    seo: {
      title: "i-Ready Math Prep for Kids | Sports Academy Games",
      description:
        "Help your child prepare for the i-Ready Diagnostic math test with fun, adaptive games. Understand the adaptive placement and the fall/winter/spring windows. Grades K–8.",
    },
    heroTag: "National Test · i-Ready Math Prep",
    heroHeadline: "Does your child need help preparing for the i-Ready math test?",
    heroSubhead:
      "The i-Ready Diagnostic from Curriculum Associates is an adaptive math test given a few times a year to find each child's exact level. Sports Academy Games builds that level up with fast, adaptive math games kids actually want to play.",
    gradeRange: "Grades K–8",
    heroMeta: "Grades K–8 · Curriculum Associates · Adaptive",
    topics: {
      eyebrow: "What the test covers",
      title: "The four i-Ready math domains",
      lede: "The i-Ready Diagnostic adapts across four math domains. Our games drill every one of them, grade by grade.",
      groups: [
        {
          label: "i-Ready math domains",
          items: [
            "Number & Operations",
            "Algebra & Algebraic Thinking",
            "Measurement & Data",
            "Geometry",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Build the level i-Ready measures",
      points: [
        "A free adaptive diagnostic finds your child's real level across the i-Ready math domains — an adaptive read just like i-Ready's own.",
        "Short, game-based drills target the exact skills just above your child's current placement, where real growth happens.",
        "Mastery tracking lets you see progress between the fall, winter, and spring windows, so each i-Ready season shows movement.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "How the i-Ready Diagnostic works",
      cards: [
        {
          title: "Adaptive placement",
          body: "i-Ready adjusts each question's difficulty based on the previous answer, narrowing in on your child's precise level across every math domain — so the result is a placement, not just a percentage.",
        },
        {
          title: "Three windows a year",
          body: "Most schools give the i-Ready Diagnostic in fall, winter, and spring. The value is in the growth between windows, which is why steady practice between tests beats cramming before one.",
        },
        {
          title: "A placement, not a grade",
          body: "i-Ready reports a placement level by domain, showing where your child is on-grade, above, or below. That domain-by-domain read is exactly how our diagnostic frames a starting point too.",
        },
        {
          title: "Now i-Ready Inform",
          body: "Curriculum Associates is rebranding the i-Ready assessment experience as i-Ready Inform beginning in the 2026–2027 school year. The adaptive math diagnostic and the skills it measures stay the same — only the name changes.",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See your child's level before the next",
      titleStripe: "i-Ready.",
      sub: "Take a free adaptive diagnostic — no signup, no card. We'll find your child's level and the games that grow it.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
  },
];

/**
 * Every test-landing config: the 4 bespoke (first) ++ the 61 generated.
 * Everything downstream (route, prerender, SEO head, regression test) reads
 * this single combined array, so the bespoke/generated split is invisible to
 * consumers.
 */
export const TEST_LANDING_CONFIGS: TestLandingConfig[] = [
  ...BESPOKE_TEST_LANDING_CONFIGS,
  ...GENERATED_TEST_LANDING_CONFIGS,
];

/**
 * Slug → config lookup used by the dynamic route. Built with a collision guard
 * because `Object.fromEntries` would silently keep only the last entry on a
 * duplicate slug, making the earlier page unreachable with no warning (a real
 * duplicate — IAR — already slipped through once). Fail loudly at module load
 * so the dupe is caught at build/test time, not in production.
 */
export const TEST_LANDING_BY_SLUG: Record<string, TestLandingConfig> =
  TEST_LANDING_CONFIGS.reduce<Record<string, TestLandingConfig>>((acc, c) => {
    if (acc[c.slug]) {
      throw new Error(
        `Duplicate test-landing slug "${c.slug}" — every TestLandingConfig.slug must be unique.`,
      );
    }
    acc[c.slug] = c;
    return acc;
  }, {});
